import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Button, CloseButton, IconButton } from '@ui/shared/Button';
import {
  closeInnerPopup,
  closePopup,
  closeTopLevelPopup,
  openInnerPopup,
  openPopup,
  openTopLevelPopup,
} from '../../../data/providers/popupsProvider';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { Icon } from '../../shared/Icon';

import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';

import { LinearLoading } from '../../shared/loading/LinearLoading';

import { CardBody } from '../../shared/card/CardBody';
import { FormContainer } from '../../shared/form/FormContainer';

import { IAthlete, ISelectableEntity, Level } from '../../../data/types';
import { mergeAthlete } from '../../../data/writers/athletes';
import { CardFooter, CardFooterRight } from '../../shared/card/CardFooter';
import { Card } from '../card/Card';
import { ControlLabel } from './ControlLabel';
import { AthleteSelect } from './selects/AthleteSelect';

interface IPopupProps {
  onClose: () => void;
  level: Level;
  athlete: IAthlete;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [saving, setSaving] = React.useState<boolean>(false);
  const [selectedToAthlete, setSelectedToAthlete] =
    React.useState<ISelectableEntity>();

  const Container = props.level === 1 ? InnerPopupCard : Card;

  const save = React.useCallback(async (): Promise<void> => {
    setSaving(true);

    if (props.athlete?.id && selectedToAthlete?.value) {
      await mergeAthlete({
        fromAthleteId: props.athlete.id,
        toAthleteId: selectedToAthlete.value,
      });
      setSaving(false);
      props.onClose();
    } else {
      setSaving(false);
      return;
    }
  }, [selectedToAthlete]);

  return (
    <Container size='medium' onClose={props.onClose} noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Merge Athlete</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving && <LinearLoading />}
          <FormContainer>
            <ControlLabel>Merge {props.athlete.name} into...</ControlLabel>
            <AthleteSelect
              value={selectedToAthlete}
              onChange={setSelectedToAthlete}
              excludedList={[props.athlete.id]}
            />
            {!saving ? (
              <div
                style={{
                  border: '1px solid var(--color-text)',
                  padding: '10px',
                  marginTop: '10px',
                }}
              >
                This will permenently disable {props.athlete.name} and merge all
                of their data into{' '}
                {selectedToAthlete
                  ? selectedToAthlete.entity.name
                  : 'the selected athlete'}
                . Please double check you have selected the correct athlete
                before proceeding. This action can not be undone.
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid var(--color-danger)',
                  padding: '10px',
                  marginTop: '10px',
                  fontSize: '1.2em',
                }}
              >
                Please do not close this window or exit the page until the merge
                is complete.
              </div>
            )}
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardHeaderLeft />
          <CardFooterRight>
            <Button
              onClick={save}
              title='Perform Merge'
              disabled={saving === true}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </Container>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  level: Level;
  athlete: IAthlete;
  iconButton?: boolean;
  onClose?: () => void;
}

export const AthleteMerger = withRouter((props: IProps): React.ReactElement => {
  const open = (): void => {
    if (props.level === 1) {
      openInnerPopup(
        <Popup
          athlete={props.athlete}
          onClose={(): void => {
            closeInnerPopup(props.history)();
            if (props.onClose) {
              props.onClose();
            }
          }}
          level={props.level}
        />,
        props.history,
        'merge-athlete'
      );
    } else if (props.level === 0) {
      openPopup(
        <Popup
          athlete={props.athlete}
          onClose={(): void => {
            closePopup(props.history)();
            if (props.onClose) {
              props.onClose();
            }
          }}
          level={props.level}
        />,
        props.history,
        'merge-athlete'
      );
    } else {
      openTopLevelPopup(
        <Popup
          athlete={props.athlete}
          onClose={(): void => {
            closeTopLevelPopup();
            if (props.onClose) {
              props.onClose();
            }
          }}
          level={props.level}
        />
      );
    }
  };

  return props?.iconButton ? (
    <IconButton onClick={open} icon='merge' title='Merge Athlete' />
  ) : (
    <Button
      title='Merge Athlete'
      icon='merge'
      onClick={open}
    />
  );
});
